import { useEffect, useRef, useState } from "react";
import {
  Gift, ShieldCheck, Check, Clock, Star, Lock,
  ArrowRight, Zap, Copy, CheckCheck, Ticket, Trophy, Users,
} from "lucide-react";
import { useCountdown } from "../hooks/useCountdown";
import { trackEvent } from "../tracking";

const SULEKHA_URL =
  "https://events.sulekha.com/ram-in-raleigh-ram-miriyala-live-concert_event-in_raleigh-nc_399249?utm_source=meta&utm_medium=paid&utm_campaign=ramraleigh&utm_content=nav_cta";
const PROMO_CODE = "DAARUDESIRALEIGH";

/* ── Urgency strip ── */
function UrgencyStrip() {
  const { hours, mins, secs } = useCountdown();
  return (
    <div className="w-full bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 py-2.5 px-4 text-center sticky top-0 z-50 shadow-[0_2px_20px_rgba(245,158,11,0.4)]">
      <p className="text-black text-[11px] font-black tracking-widest uppercase flex items-center justify-center gap-2">
        <Zap className="w-3 h-3 flex-shrink-0" />
        MAY 9 · 9PM — SHOW STARTS IN&nbsp;
        <span className="font-mono">
          {String(hours).padStart(2, "0")}h {String(mins).padStart(2, "0")}m {String(secs).padStart(2, "0")}s
        </span>
        <Zap className="w-3 h-3 flex-shrink-0" />
      </p>
    </div>
  );
}

/* ── Prominent code display with copy ── */
function CodeCard() {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(PROMO_CODE).catch(() => {});
    setCopied(true);
    trackEvent("PromoCodeCopied", { content_name: "code_card" });
    setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div className="relative">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/40 to-[#C9ED00]/40 rounded-2xl blur opacity-80" />
      <div className="relative bg-black border border-[#C9ED00]/40 rounded-2xl p-5 shadow-[0_0_30px_rgba(201,237,0,0.1)]">
        <p className="text-white/30 text-[9px] tracking-[4px] uppercase mb-3 text-center">Your promo code</p>
        <p className="font-mono font-black text-2xl tracking-[0.25em] text-[#C9ED00] text-center mb-4 break-all">
          {PROMO_CODE}
        </p>
        <button
          onClick={handleCopy}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#C9ED00]/10 border border-[#C9ED00]/30 rounded-xl text-[#C9ED00] text-sm font-bold hover:bg-[#C9ED00]/20 transition-all"
        >
          {copied
            ? <CheckCheck className="w-4 h-4 flex-shrink-0" />
            : <Copy className="w-4 h-4 flex-shrink-0" />
          }
          {copied ? "Copied to clipboard!" : "Tap to copy"}
        </button>
        <p className="text-white/20 text-[10px] text-center mt-2 tracking-widest uppercase">
          Enter this at Sulekha checkout · $5 off applied instantly
        </p>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function RamGiftCardLanding() {
  const { hours, mins, secs } = useCountdown();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackEvent("GiftCardLandingPageView", {
      content_name: "ram_gift_card_landing",
      content_category: "gift_card_offer",
      value: 20,
      currency: "USD",
    });
  }, []);

  function handleBuy(step) {
    trackEvent("GiftCardSulekhaClick", { content_name: "gift_card_landing", step });
    window.open(SULEKHA_URL, "_blank");
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col overflow-x-hidden">

      <UrgencyStrip />

      {/* background */}
      <div className="fixed inset-0 pointer-events-none select-none z-0">
        <img src="/heroimage.jpeg" alt="" className="w-full h-full object-cover object-top opacity-20 grayscale" aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/50 to-[#050505]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 20%, rgba(245,158,11,0.12) 0%, transparent 60%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 80% 80%, rgba(201,237,0,0.07) 0%, transparent 50%)" }} />
      </div>

      <main className="relative z-10 flex-1 container mx-auto px-5 md:px-6 max-w-2xl pb-40">

        {/* ── HERO ── */}
        <section className="pt-10 pb-12 text-center">

          {/* badges */}
          <div className="flex justify-center gap-2 flex-wrap mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
              <Trophy className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[10px] font-black tracking-[0.2em] text-amber-400 uppercase">Daaru Desi × xpat.money</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-full">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-red-400 text-[11px] font-black tracking-widest uppercase">May 9 · 9PM</span>
            </div>
          </div>

          {/* social proof */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <Users className="w-3.5 h-3.5 text-[#C9ED00] flex-shrink-0" />
            <span className="text-white/70 text-xs font-bold">
              <span className="text-[#C9ED00]">300 tickets</span> already booked — Enigma holds 700
            </span>
          </div>

          {/* glowing gift icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-[#C9ED00]/10 blur-2xl animate-pulse" />
              <div className="absolute -inset-3 rounded-full bg-amber-500/10 blur-xl" />
              <div className="relative w-20 h-20 rounded-3xl bg-[#C9ED00] flex items-center justify-center shadow-[0_0_40px_rgba(201,237,0,0.4)]">
                <Gift className="w-10 h-10 text-black" />
              </div>
            </div>
          </div>

          <h1 className="font-serif leading-[0.85] tracking-tight mb-3" style={{ fontSize: "clamp(3.5rem, 13vw, 5.5rem)" }}>
            <span className="text-[#C9ED00]">$5 OFF</span>
            <br />
            <span className="text-white">YOUR TICKET</span>
          </h1>

          <p className="text-white/50 text-base leading-relaxed mb-6">
            Use the code below at Sulekha checkout — $5 off applied instantly.
          </p>

          {/* guaranteed double win */}
          <div className="relative mb-6 text-left">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-[#C9ED00]/20 rounded-2xl blur opacity-60" />
            <div className="relative bg-[#0d0d0d] border border-white/10 rounded-2xl p-4">
              <p className="text-white/30 text-[10px] uppercase tracking-[3px] mb-3 text-center">You get both. Guaranteed.</p>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-black" />
                    </div>
                    <span className="text-white text-sm font-medium">Use code at Sulekha checkout</span>
                  </div>
                  <span className="text-amber-400 font-black text-sm whitespace-nowrap">$5 off</span>
                </div>
                <div className="h-px bg-white/5" />
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#C9ED00] flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-black" />
                    </div>
                    <span className="text-white text-sm font-medium">Show up May 9, 9PM at the event</span>
                  </div>
                  <span className="text-[#C9ED00] font-black text-sm whitespace-nowrap">+$20 gift card</span>
                </div>
              </div>
              <p className="text-white/25 text-[11px] text-center mt-3 leading-relaxed">
                Both are yours — no catch, no KYC. New xpat members only.
              </p>
            </div>
          </div>

          {/* code card */}
          <div className="mb-5">
            <CodeCard />
          </div>

          {/* buy CTA */}
          <button
            onClick={() => handleBuy("hero")}
            className="w-full flex items-center justify-center gap-2 py-5 bg-amber-500 text-black rounded-full font-black text-base md:text-lg shadow-[0_0_40px_rgba(245,158,11,0.35)] hover:bg-amber-400 hover:scale-[1.02] transition-all duration-200"
            style={{ animation: "cta-pulse 2.5s infinite" }}
          >
            <Ticket className="w-5 h-5 flex-shrink-0" />
            Buy Ticket on Sulekha — $5 Off
            <ArrowRight className="w-5 h-5 flex-shrink-0" />
          </button>
          <p className="text-white/20 text-[10px] tracking-widest uppercase mt-3">
            $5 off at checkout · $20 gift card at the door · New xpat members only
          </p>

          <div className="mt-4 flex items-start gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-left">
            <ShieldCheck className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-white/50 text-[11px] leading-relaxed">
              <span className="text-white font-bold">$20 gift card for new xpat members only.</span>{" "}
              Existing xpat.money account holders are not eligible.
            </p>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="mb-10">
          <p className="text-[10px] font-bold tracking-[3px] text-amber-500 uppercase text-center mb-5">How it works</p>
          <div className="space-y-3">
            {[
              {
                n: "1", hi: true,
                title: "Copy the code → buy on Sulekha",
                desc: "Use DAARUDESIRALEIGH at checkout — $5 off applied automatically. Ticket confirmation hits your email instantly.",
              },
              {
                n: "2", hi: false,
                title: "Show up May 9, 9PM → walk out $20 richer",
                desc: "New xpat members receive a physical $20 gift card at the door. Redeem it in the app in 2 minutes — money lands straight in your wallet.",
              },
            ].map(({ n, hi, title, desc }) => (
              <div key={n} className={`flex gap-4 items-start p-4 rounded-2xl ${hi ? "bg-amber-500/10 border border-amber-500/20" : "bg-white/5 border border-white/10"}`}>
                <div className={`w-8 h-8 rounded-full font-black text-sm flex items-center justify-center flex-shrink-0 ${hi ? "bg-amber-500 text-black" : "bg-white/10 text-white/50"}`}>{n}</div>
                <div>
                  <p className={`font-bold text-sm mb-0.5 ${hi ? "text-white" : "text-white/80"}`}>{title}</p>
                  <p className="text-white/40 text-xs leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── GIFT CARD SECTION ── */}
        <section className="mb-10">
          <div className="bg-[#0d0d0d] border border-[#C9ED00]/20 rounded-[2rem] overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,237,0,0.08) 0%, transparent 70%)", filter: "blur(20px)" }} />

            {/* gift card image */}
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-2 mb-1">
                <Gift className="w-4 h-4 text-[#C9ED00]" />
                <p className="text-[10px] font-bold tracking-[3px] text-[#C9ED00] uppercase">xpat.money</p>
              </div>
              <h3 className="text-white font-black text-lg leading-tight mb-4">Your $20 gift card at the door</h3>
            </div>

            <img
              src="/xpat-gift-card.png"
              alt="xpat.money $20 gift card — how to redeem"
              className="w-full object-cover"
            />

            <div className="px-6 py-5">
              <div className="bg-[#C9ED00]/5 border border-[#C9ED00]/20 rounded-2xl px-4 py-3">
                <p className="text-white/50 text-xs leading-relaxed">
                  🎁 <span className="text-white font-bold">New xpat members only.</span>{" "}
                  Existing xpat.money accounts are not eligible for the gift card.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── URGENCY ── */}
        <section className="mb-10">
          <div className="bg-white/5 border border-amber-500/20 rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-4 justify-center">
              <Clock className="w-4 h-4 text-amber-400" />
              <p className="text-amber-400 text-[10px] font-black tracking-[3px] uppercase">May 9 · 9PM · Enigma Nightclub</p>
            </div>
            <div className="flex justify-center gap-3 mb-5">
              {[{ v: hours, l: "Hours" }, { v: mins, l: "Mins" }, { v: secs, l: "Secs" }].map(({ v, l }) => (
                <div key={l} className="flex flex-col items-center">
                  <div className="text-3xl md:text-4xl font-serif font-bold bg-white/5 border border-white/10 rounded-2xl px-4 py-3 min-w-[60px] text-center text-amber-400">
                    {String(v).padStart(2, "0")}
                  </div>
                  <p className="text-[10px] uppercase tracking-[2px] text-white/30 mt-1.5">{l}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2 mb-5">
              {[
                "🎁  Gift cards at the door — while supplies last",
                "🔴  Offer expires when show starts — May 9, 9PM",
                "⚠️  300 tickets sold — 400 spots left, moving fast",
                "⚡  Ticket confirmed to email — no pickup needed",
              ].map(line => (
                <p key={line} className="text-white/50 text-xs text-center leading-relaxed">{line}</p>
              ))}
            </div>
            <button
              onClick={() => handleBuy("urgency")}
              className="w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-black rounded-full font-black text-sm hover:bg-amber-400 transition-all"
            >
              <Ticket className="w-4 h-4 flex-shrink-0" />
              Yes — $5 Off Now & $20 At The Door
            </button>
          </div>
        </section>

        {/* ── TRUST ── */}
        <section className="mb-10 flex flex-col items-center gap-3">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
            <span className="text-white/50 text-xs ml-2 font-medium">RAM Miriyala live — May 9, 9PM</span>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-3 text-xs text-white/30 font-medium">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Secure checkout</span>
            <span>·</span>
            <span>🛡️ FDIC-backed xpat wallet</span>
            <span>·</span>
            <span>18+ event</span>
            <span>·</span>
            <span>New members only</span>
          </div>
        </section>

      </main>

      {/* ── STICKY MOBILE CTA ── */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden px-4 pb-6 pt-4"
        style={{ background: "linear-gradient(to top, #050505 65%, transparent)" }}
      >
        <button
          onClick={() => handleBuy("sticky")}
          className="w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-black rounded-full font-black text-sm shadow-[0_0_30px_rgba(245,158,11,0.4)]"
          style={{ animation: "cta-pulse 2.5s infinite" }}
        >
          <Ticket className="w-4 h-4 flex-shrink-0" />
          Buy Ticket — Code DAARUDESIRALEIGH
        </button>
        <p className="text-center text-white/25 text-[9px] tracking-widest uppercase mt-2">
          ⏰ {String(hours).padStart(2, "0")}h {String(mins).padStart(2, "0")}m left · May 9, 9PM
        </p>
      </div>

      <style>{`
        @keyframes cta-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(245,158,11,0.45); }
          70%  { box-shadow: 0 0 0 16px rgba(245,158,11,0); }
          100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); }
        }
      `}</style>
    </div>
  );
}
